/**
 * Story 053 — Users list page (`/admin/users`).
 *
 * MVP-carve: lives in `apps/web/src/features/admin/` per UX brief §1.1.
 * The DataGrid here is a plain semantic `<table role="grid">` with
 * accessible row names — Playwright `getByRole("row", { name: /alice/i })`
 * resolves cleanly without depending on MUI X DataGrid's heavier ARIA
 * mapping. Pagination + search + role + status filters all live in the
 * URL search-params so admin links are bookmarkable (Story 017 pattern).
 *
 * The detail drawer slides in from the right when ?user=<id> is set.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { useMe } from "@aeriosiq/auth-client";
import {
  useAdminUsersQuery,
  deriveUserStatus,
  useChangeTenantUserRoleMutation,
  type AdminUser,
  type ListUsersParams,
  type Role,
  type UserStatus,
} from "../queries/index.js";
import { ToastProvider, useToast } from "../../account/ToastContext.js";
import { RoleChip } from "../shared/RoleChip.js";
import { StatusChip } from "../shared/StatusChip.js";
import { relativeTime, roleLabel } from "../shared/formatters.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { UserDetailDrawer } from "./UserDetailDrawer.js";
import { InviteUserDialog } from "./InviteUserDialog.js";
// TNCY-5 (XT-D26) — pending USER-invites table relocated onto this page.
import { PendingInvitationsSection } from "../invitations/PendingInvitationsSection.js";
import {
  PLATFORM_ADMIN_ASSIGNABLE_ROLES,
  TENANT_ADMIN_ASSIGNABLE_ROLES,
} from "./constants.js";
import { CheckIcon, ChevronDownIcon } from "@aeriosiq/ui/icons";

// Story 054a — adds project_manager + back_office. The "Role" FilterMenu
// kept from 053 still drives the canonical filter affordance; the
// adjacent quick-toggle chip strip mirrors the same options as
// `role="button"` shortcuts (AC5 asserts on them as buttons rather than
// menu options). Platform admin is intentionally absent — tenant-side
// admin surfaces never filter on it (it lives at the org tier).
const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "expert_analyst", label: "Expert Analyst" },
  { value: "pre_screener", label: "Pre-Screener" },
  { value: "project_manager", label: "Project Manager" },
  { value: "back_office", label: "Back Office" },
  { value: "pilot", label: "Pilot" },
  { value: "tenant_admin", label: "Tenant Admin" },
];

const STATUS_OPTIONS: { value: UserStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "blocked", label: "Blocked" },
  { value: "deleted", label: "Removed" },
];

export function UsersListPage(): ReactElement {
  return (
    <ToastProvider>
      <UsersListPageInner />
    </ToastProvider>
  );
}

function UsersListPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const userIdInUrl = params.get("user");
  const dialogParam = params.get("dialog");
  const inviteOpen = dialogParam === "invite";

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);
  // When debounced search changes, update the URL.
  useEffect(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        // Reset page on filter change.
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  // SB6-ADM-USERS-01 — `showExternal` is sticky in the URL so a deep-link
  // shared between admins surfaces the same filter set. Default off.
  const showExternal = params.get("showExternal") === "true";
  const queryParams = useMemo<ListUsersParams>(
    () => ({
      page: Number(params.get("page") ?? "1"),
      pageSize: 25,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      role: (params.getAll("role") as Role[]).filter((r) =>
        ROLE_OPTIONS.some((o) => o.value === r),
      ),
      status: (params.getAll("status") as UserStatus[]).filter((s) =>
        STATUS_OPTIONS.some((o) => o.value === s),
      ),
      showExternal,
    }),
    [params, debouncedSearch, showExternal],
  );

  const { data, isPending, isError } = useAdminUsersQuery(queryParams);

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 25));

  function openUser(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("user", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeUser(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("user");
        return next;
      },
      { replace: false },
    );
  }
  function openInvite(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("dialog", "invite");
        return next;
      },
      { replace: false },
    );
  }
  function closeInvite(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("dialog");
        return next;
      },
      { replace: false },
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <PageHeading>Users</PageHeading>
          {/* SB4-04 / decision L3: template-page convention is H1 with
              no sub-header. Christian at [00:33:31]: "there is a
              dedicated H1 header. There is no sub header". */}
        </Box>
        <Button variant="contained" onClick={openInvite}>
          Invite user
        </Button>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, alignItems: "flex-start" }}
      >
        <TextField
          slotProps={{
            htmlInput: {
              "aria-label": "Search by name or email",
              type: "search",
            },
          }}
          placeholder="Search by name or email"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          size="small"
          sx={{ width: 320 }}
        />
        <FilterMenu
          label="Role"
          options={ROLE_OPTIONS}
          selected={params.getAll("role")}
          onToggle={(value) => {
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                const current = next.getAll("role");
                next.delete("role");
                if (current.includes(value)) {
                  for (const v of current.filter((c) => c !== value)) {
                    next.append("role", v);
                  }
                } else {
                  for (const v of [...current, value]) {
                    next.append("role", v);
                  }
                }
                next.delete("page");
                return next;
              },
              { replace: true },
            );
          }}
        />
        <FilterMenu
          label="Status"
          options={STATUS_OPTIONS}
          selected={params.getAll("status")}
          onToggle={(value) => {
            setParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                const current = next.getAll("status");
                next.delete("status");
                if (current.includes(value)) {
                  for (const v of current.filter((c) => c !== value)) {
                    next.append("status", v);
                  }
                } else {
                  for (const v of [...current, value]) {
                    next.append("status", v);
                  }
                }
                next.delete("page");
                return next;
              },
              { replace: true },
            );
          }}
        />
        {/* SB6-ADM-USERS-01 — external collaborators toggle. */}
        <FormControlLabel
          control={
            <Switch
              checked={showExternal}
              onChange={(_e, checked) => {
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    if (checked) {
                      next.set("showExternal", "true");
                    } else {
                      next.delete("showExternal");
                    }
                    next.delete("page");
                    return next;
                  },
                  { replace: true },
                );
              }}
              inputProps={{ "aria-label": "Show external collaborators" }}
            />
          }
          label="Show external collaborators"
          sx={{ ml: 1 }}
        />
      </Stack>

      {/* Story 054a — quick-toggle chip strip for role filters. Each
          role renders as a `role="button"` toggle that mirrors the URL
          state of the canonical Role FilterMenu above. AC5 asserts on
          these buttons by visible name. Clicking toggles the role in
          the `role=` search param. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2, flexWrap: "wrap" }}
        aria-label="Quick role filters"
      >
        {ROLE_OPTIONS.map((opt) => {
          const isSelected = params.getAll("role").includes(opt.value);
          return (
            <Button
              key={opt.value}
              size="small"
              variant={isSelected ? "contained" : "outlined"}
              aria-pressed={isSelected}
              onClick={() => {
                setParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    const current = next.getAll("role");
                    next.delete("role");
                    if (current.includes(opt.value)) {
                      for (const v of current.filter((c) => c !== opt.value)) {
                        next.append("role", v);
                      }
                    } else {
                      for (const v of [...current, opt.value]) {
                        next.append("role", v);
                      }
                    }
                    next.delete("page");
                    return next;
                  },
                  { replace: true },
                );
              }}
            >
              {opt.label}
            </Button>
          );
        })}
      </Stack>

      {isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Couldn&apos;t load users.</Typography>
      ) : (
        <UsersGrid
          users={data.items}
          onOpen={openUser}
          showExternal={showExternal}
        />
      )}

      {!isPending && !isError && data.total > 25 ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Pagination
            page={queryParams.page ?? 1}
            count={totalPages}
            onChange={(_e, p) => {
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("page", String(p));
                  return next;
                },
                { replace: true },
              );
            }}
            color="primary"
          />
        </Stack>
      ) : null}

      {/* TNCY-5 (XT-D26) — pending USER invitations now live on the Users
          page (relocated from the retired Administration → Invitations
          leaf). Shown beneath the user list so all user management is in
          one place. */}
      <PendingInvitationsSection />

      <UserDetailDrawer userId={userIdInUrl} onClose={closeUser} />
      <InviteUserDialog open={inviteOpen} onClose={closeInvite} />
    </Box>
  );
}

interface UsersGridProps {
  users: AdminUser[];
  onOpen: (id: string) => void;
  /** SB6-ADM-USERS-01 — render the Tenant column when the toggle is on. */
  showExternal: boolean;
}

function UsersGrid({
  users,
  onOpen,
  showExternal,
}: UsersGridProps): ReactElement {
  if (users.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          color: "text.secondary",
        }}
      >
        <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
          No users match
        </Typography>
        <Typography variant="body2">
          Try clearing your filters or inviting a new user.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="table"
      role="grid"
      aria-label="Users in your tenant"
      sx={{
        width: "100%",
        borderCollapse: "collapse",
        "& th, & td": {
          textAlign: "left",
          py: 1.5,
          px: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          fontSize: 14,
        },
        "& th": {
          color: "text.secondary",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        },
        "& tr[role='row']": {
          cursor: "pointer",
          transition: "background-color 120ms",
        },
        "& tr[role='row']:hover": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.02)",
        },
      }}
    >
      <thead>
        <tr role="row">
          <th role="columnheader" scope="col" aria-label="Avatar"></th>
          <th role="columnheader" scope="col">
            Name
          </th>
          <th role="columnheader" scope="col">
            Role
          </th>
          <th role="columnheader" scope="col">
            Status
          </th>
          {/* SB6-ADM-USERS-01 — Tenant column conditional on toggle. */}
          {showExternal ? (
            <th role="columnheader" scope="col">
              Tenant
            </th>
          ) : null}
          <th role="columnheader" scope="col">
            Last login
          </th>
          <th role="columnheader" scope="col">
            Sessions
          </th>
          {/* Story 054a — Actions column hosts the row-level role-change
              button. Kept at the far right so the row's center-of-click
              hit area (which Playwright uses for getByRole("row").click())
              still lands on the Name cell and opens the detail drawer. */}
          <th role="columnheader" scope="col" aria-label="Actions"></th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => {
          const status = deriveUserStatus(u);
          // Accessible row name: display name + email + role + status
          // so getByRole("row", { name: /alice/i }) matches by first name.
          const rowName = `${u.displayName} ${u.email} ${u.role} ${status}`;
          return (
            <tr
              key={u.id}
              role="row"
              aria-label={rowName}
              tabIndex={0}
              onClick={() => {
                onOpen(u.id);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(u.id);
                }
              }}
              data-user-id={u.id}
            >
              <td>
                <Avatar
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 32, height: 32, fontSize: 13 }}
                >
                  {u.initials}
                </Avatar>
              </td>
              <td>
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight={500}>
                    {u.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                </Stack>
              </td>
              <td>
                <RoleChip role={u.role} />
              </td>
              <td>
                <StatusChip status={status} tenantName={u.tenantName ?? null} />
              </td>
              {/* SB6-ADM-USERS-01 — Tenant column body. */}
              {showExternal ? (
                <td>
                  <Typography variant="body2" color="text.secondary">
                    {u.tenantName ?? "—"}
                  </Typography>
                </td>
              ) : null}
              <td>
                <Tooltip title={u.lastLoginAt ?? ""} placement="top">
                  <span>{relativeTime(u.lastLoginAt)}</span>
                </Tooltip>
              </td>
              <td style={{ textAlign: "right" }}>
                <Typography
                  variant="body2"
                  color={
                    u.activeSessionCount === 0
                      ? "text.secondary"
                      : "text.primary"
                  }
                >
                  {u.activeSessionCount}
                </Typography>
              </td>
              <td style={{ textAlign: "right" }}>
                <RoleChangeButton user={u} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

/**
 * Story 054a — per-row role-change affordance.
 *
 * Renders a small "Change role" button in the role column. Clicking
 * opens a menu listing the four tenant-side roles; selecting one opens
 * a ConfirmDialog and, on confirm, fires the PATCH /users/:id/role
 * mutation. The drawer-based role change (Story 053) remains the
 * canonical detail surface; this is the quick-mutate shortcut from the
 * list view.
 *
 * Platform Admin is NOT offered — role escalation is rejected at the
 * BE (`role_escalation_forbidden`), so we hide the option client-side
 * to keep the menu coherent.
 */
function RoleChangeButton({ user }: { user: AdminUser }): ReactElement {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pendingRole, setPendingRole] = useState<Role | null>(null);
  // SB2-W-01 — wire to the new tenant-scope mutation. The legacy
  // /users/{id}/role endpoint still works; the new endpoint is
  // semantically cleaner because the SoD predicate runs server-side
  // and a forbidden role surfaces as 403 tenant_admin_promotion_forbidden.
  const updateRole = useChangeTenantUserRoleMutation();
  const me = useMe();
  const callerRoles = me.data?.roles ?? [];
  const callerIsTenantAdmin =
    callerRoles.includes("tenant_admin") &&
    !callerRoles.includes("platform_admin");
  const assignableRoles = callerIsTenantAdmin
    ? TENANT_ADMIN_ASSIGNABLE_ROLES
    : PLATFORM_ADMIN_ASSIGNABLE_ROLES;
  const { toast } = useToast();
  const open = anchor !== null;

  function close(): void {
    setAnchor(null);
  }

  async function confirm(): Promise<void> {
    if (pendingRole === null) return;
    try {
      await updateRole.mutateAsync({ id: user.id, role: pendingRole });
      toast(`Role changed to ${roleLabel(pendingRole)}.`);
    } catch (e) {
      const status = (e as { status?: number }).status;
      const reason = (e as { body?: { reason?: string } }).body?.reason;
      if (status === 403 && reason === "tenant_admin_promotion_forbidden") {
        toast(
          "tenant_admin_promotion_forbidden — only a platform admin can grant tenant-admin or platform-admin roles (ISO 27001 A.6.1.2 separation-of-duties).",
          "error",
        );
      } else {
        toast("Couldn't change role.", "error");
      }
    } finally {
      setPendingRole(null);
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="text"
        aria-label="Change role"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          // The surrounding row has an onClick that opens the drawer;
          // stop the bubble so opening the role menu doesn't also
          // navigate.
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        Change role
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        MenuListProps={{ role: "menu" }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {assignableRoles.map((roleValue) => (
          <MenuItem
            key={roleValue}
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              close();
              setPendingRole(roleValue);
            }}
            disabled={roleValue === user.role}
          >
            {roleLabel(roleValue)}
          </MenuItem>
        ))}
      </Menu>
      <ConfirmDialog
        open={pendingRole !== null}
        title="Change role?"
        description={
          pendingRole !== null
            ? `Change ${user.displayName}'s role from ${roleLabel(user.role)} to ${roleLabel(pendingRole)}?`
            : ""
        }
        confirmLabel="Confirm role change"
        pending={updateRole.isPending}
        onConfirm={() => {
          void confirm();
        }}
        onCancel={() => {
          setPendingRole(null);
        }}
      />
    </>
  );
}

interface FilterMenuProps {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}

function FilterMenu({ label, options, selected, onToggle }: FilterMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;
  return (
    <>
      <Button
        onClick={(e) => {
          setAnchor(e.currentTarget);
        }}
        variant="outlined"
        size="small"
        aria-haspopup="listbox"
        aria-expanded={open}
        endIcon={<ChevronDownIcon />}
        sx={{ minWidth: 120 }}
      >
        {label}
        {selected.length > 0 ? ` (${String(selected.length)})` : ""}
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => {
          setAnchor(null);
        }}
        MenuListProps={{ role: "listbox" }}
      >
        {options.map((o) => {
          const isSel = selected.includes(o.value);
          return (
            <MenuItem
              key={o.value}
              role="option"
              aria-selected={isSel}
              onClick={() => {
                onToggle(o.value);
              }}
              data-value={o.value}
            >
              {isSel ? (
                <CheckIcon fontSize="small" sx={{ mr: 1 }} />
              ) : (
                <Box sx={{ width: 24, mr: 1 }} />
              )}
              {o.label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

// Pulled out of UsersListPageInner to keep the export clean.
// Wrapping IconButton with hidden mode for the "More" menu.
export function MoreIcon(): ReactElement {
  return (
    <IconButton size="small" aria-label="More actions">
      <Box component="span" sx={{ fontSize: 16 }}>
        ⋯
      </Box>
    </IconButton>
  );
}
